// frontend/src/components/layout/SecondarySidebarSlot.tsx
import * as React from 'react';

import { createPortal } from 'react-dom';

/**
 * Portal slot for rendering a secondary sidebar at the AppShell flex-row level,
 * so it spans full viewport height (alongside the primary sidebar, not inside
 * the topbar+content column).
 *
 * Usage:
 * 1. AppShell wraps its content with <SecondarySidebarSlotProvider>
 * 2. Page layouts call <SecondarySidebarPortal>...</SecondarySidebarPortal>
 *    to render their sidebar into the slot
 */

const SecondarySidebarSlotContext = React.createContext<HTMLDivElement | null>(null);

type MobileNavSlot = {
  node: HTMLDivElement | null;
  setNode: (node: HTMLDivElement | null) => void;
};
const MobileNavSlotContext = React.createContext<MobileNavSlot>({
  node: null,
  setNode: () => undefined,
});

export function SecondarySidebarSlotProvider({ children }: { children: React.ReactNode }) {
  const [slotNode, setSlotNode] = React.useState<HTMLDivElement | null>(null);
  const [mobileNavNode, setMobileNavNode] = React.useState<HTMLDivElement | null>(null);
  const mobileNav = React.useMemo(
    () => ({ node: mobileNavNode, setNode: setMobileNavNode }),
    [mobileNavNode],
  );

  return (
    <SecondarySidebarSlotContext.Provider value={slotNode}>
      <MobileNavSlotContext.Provider value={mobileNav}>
        <div ref={setSlotNode} className="hidden md:flex" />
        {children}
      </MobileNavSlotContext.Provider>
    </SecondarySidebarSlotContext.Provider>
  );
}

/**
 * Where a section puts its phone navigation: the right end of the topbar, so the
 * sections of the current area take no row of their own under it.
 */
export function SecondaryNavMobileSlot() {
  const { setNode } = React.useContext(MobileNavSlotContext);
  return (
    <div ref={setNode} className="ml-auto flex shrink-0 items-center empty:hidden md:hidden" />
  );
}

/** True inside the app shell, where the topbar offers a place for the trigger. */
export function useSecondaryNavMobileSlot(): boolean {
  return React.useContext(MobileNavSlotContext).node !== null;
}

/** Renders the phone navigation trigger of the current area into the topbar. */
export function SecondaryNavMobilePortal({ children }: { children: React.ReactNode }) {
  const { node } = React.useContext(MobileNavSlotContext);
  return node ? createPortal(children, node) : null;
}

/**
 * Renders children into the secondary sidebar slot at the AppShell level.
 * Returns null if slot is not mounted (e.g. mobile, or outside AppShell).
 */
export function SecondarySidebarPortal({ children }: { children: React.ReactNode }) {
  const slotNode = React.useContext(SecondarySidebarSlotContext);

  if (!slotNode) return null;
  return createPortal(children, slotNode);
}
